/**
 * GrainOverlay - Subtle film grain texture for luxury feel
 *
 * Creates an SVG-based noise pattern overlay that adds tactile realism
 * to flat digital surfaces. Very subtle - just enough to avoid the
 * "too clean" digital look.
 *
 * Based on CSS-Tricks grainy gradients technique:
 * https://css-tricks.com/grainy-gradients/
 */

import { memo } from "react";

export interface GrainOverlayProps {
  /** Opacity of the grain effect (0-1). Default: 0.03 */
  opacity?: number;
  /** Whether to animate the grain (subtle flicker). Default: false */
  animate?: boolean;
  /** Z-index for layering. Default: 1 */
  zIndex?: number;
}

function GrainOverlayComponent({
  opacity = 0.03,
  animate = false,
  zIndex = 1,
}: GrainOverlayProps) {
  return (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex,
        opacity,
        mixBlendMode: "overlay",
      }}
    >
      <svg
        className="w-full h-full"
        style={{
          animation: animate ? "grain 0.5s steps(1) infinite" : undefined,
        }}
      >
        <filter id="lukhas-noise-filter">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.7"
            numOctaves="4"
            stitchTiles="stitch"
          />
          <feColorMatrix type="saturate" values="0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#lukhas-noise-filter)" />
      </svg>

      {animate && (
        <style>{`
          @keyframes grain {
            0%, 100% { transform: translate(0, 0); }
            10% { transform: translate(-1%, -1%); }
            20% { transform: translate(1%, 1%); }
            30% { transform: translate(-1%, 1%); }
            40% { transform: translate(1%, -1%); }
            50% { transform: translate(-1%, 0%); }
            60% { transform: translate(1%, 0%); }
            70% { transform: translate(0%, 1%); }
            80% { transform: translate(0%, -1%); }
            90% { transform: translate(-1%, 1%); }
          }
        `}</style>
      )}
    </div>
  );
}

// Memoize since this rarely changes
export const GrainOverlay = memo(GrainOverlayComponent);

GrainOverlay.displayName = "GrainOverlay";

/**
 * LuxuryBackground - Complete luxury background with grain, gradients, and particles
 *
 * Combines:
 * - Subtle radial gradients based on accent color
 * - Film grain overlay
 * - Floating particle system
 */
export interface LuxuryBackgroundProps {
  /** Accent color for gradients and particles */
  accentColor?: string;
  /** Whether to show the grain overlay */
  showGrain?: boolean;
  /** Whether to show floating particles */
  showParticles?: boolean;
  /** Number of particles. Default: 15 */
  particleCount?: number;
  /** Grain opacity. Default: 0.025 */
  grainOpacity?: number;
}

export function LuxuryBackground({
  accentColor = "#22D3EE",
  showGrain = true,
  showParticles = true,
  particleCount = 15,
  grainOpacity = 0.025,
}: LuxuryBackgroundProps) {
  return (
    <>
      {/* Base gradient */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 100% 80% at 50% 0%, ${accentColor}05 0%, transparent 50%),
            radial-gradient(ellipse 80% 60% at 80% 100%, ${accentColor}03 0%, transparent 40%)
          `,
          zIndex: 0,
        }}
      />

      {/* Grain overlay */}
      {showGrain && <GrainOverlay opacity={grainOpacity} />}

      {/* Subtle floating particles */}
      {showParticles && (
        <div
          className="fixed inset-0 pointer-events-none overflow-hidden"
          style={{ zIndex: 0 }}
        >
          {[...Array(particleCount)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 rounded-full"
              style={{
                background: accentColor,
                opacity: 0.1 + Math.random() * 0.15,
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animation: `float-particle ${
                  6 + Math.random() * 6
                }s ease-in-out infinite`,
                animationDelay: `${Math.random() * 6}s`,
              }}
            />
          ))}
          <style>{`
            @keyframes float-particle {
              0%, 100% { transform: translateY(0) translateX(0); opacity: 0.1; }
              25% { transform: translateY(-20px) translateX(5px); opacity: 0.25; }
              50% { transform: translateY(-40px) translateX(-5px); opacity: 0.15; }
              75% { transform: translateY(-20px) translateX(3px); opacity: 0.2; }
            }
          `}</style>
        </div>
      )}
    </>
  );
}

LuxuryBackground.displayName = "LuxuryBackground";
